/**
 * Fullscript integration for mensresearch.ai
 *
 * Provides catalog search, product lookup, and checkout creation.
 * Users complete their purchase through the Fullscript checkout URL
 * without leaving the mensresearch.ai experience.
 */

import { api } from '@/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FullscriptProduct {
  id: string;
  name: string;
  brand: string;
  imageUrl: string;
  description: string;
  price: number;
  currency: string;
  url: string;
  inStock: boolean;
  variantId: string;
}

export interface CheckoutResult {
  planId: string;
  checkoutUrl: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Search the Fullscript product catalog */
export async function searchProducts(query: string): Promise<FullscriptProduct[]> {
  const result = await api.get<{ products: FullscriptProduct[] }>(
    `/fullscript/products?q=${encodeURIComponent(query)}`,
  );
  return result.products || [];
}

/** Get a single product by ID */
export async function getProductById(productId: string): Promise<FullscriptProduct | null> {
  try {
    const result = await api.get<{ product: FullscriptProduct }>(
      `/fullscript/products/${productId}`,
    );
    return result.product;
  } catch {
    return null;
  }
}

/** Create a treatment plan checkout for the given products */
export async function createCheckout(
  products: Array<{
    productId: string;
    variantId: string;
    quantity: number;
    dosageInstructions: string;
  }>,
): Promise<CheckoutResult> {
  return api.post<CheckoutResult>('/fullscript/checkout', { products });
}

/** Check Fullscript connection status */
export async function getStatus(): Promise<{ configured: boolean; connected: boolean }> {
  return api.get<{ configured: boolean; connected: boolean }>('/fullscript/status');
}

/**
 * Open a product for checkout. If we have a Fullscript product ID and variant,
 * create a checkout. Otherwise fall back to catalog search in a new tab.
 */
export function openProduct(productUrl: string, productName: string): void {
  // productUrl field stores the fullscriptProductId (legacy column reuse)
  // For now, just open Fullscript catalog search as a fallback
  if (!productUrl) {
    window.open(
      `https://us.fullscript.com/catalog?q=${encodeURIComponent(productName)}`,
      '_blank',
      'noopener,noreferrer',
    );
    return;
  }
  // If we have a product ID, the checkout flow is handled by the modal
  window.open(
    `https://us.fullscript.com/catalog?q=${encodeURIComponent(productName)}`,
    '_blank',
    'noopener,noreferrer',
  );
}
